/**
 * Human-readable "when did this sell" label for last-sold displays. Takes the
 * ISO 8601 string the Anointed commerce API stamps on each last-item-sold
 * entry (Eastern offset, e.g. "2026-01-15T13:30:00-05:00") and renders it as
 * a short relative phrase. Older-than-a-week sales fall back to an absolute
 * Eastern-time date so the mall never shows a stale "N days ago" for months.
 *
 * Returns null when the input is falsy or unparseable, so callers can simply
 * skip rendering the line.
 */
export function relativeSoldTime(iso: string | null | undefined): string | null {
  if (!iso) {
    return null;
  }
  const parsed: number = Date.parse(iso);
  if (Number.isNaN(parsed)) {
    return null;
  }
  const diffMs: number = Date.now() - parsed;
  if (diffMs < 60_000) {
    return "just now";
  }
  const minutes: number = Math.floor(diffMs / 60_000);
  if (minutes < 60) {
    return `${minutes} ${plural(minutes, "minute")} ago`;
  }
  const hours: number = Math.floor(diffMs / 3_600_000);
  if (hours < 24) {
    return `${hours} ${plural(hours, "hour")} ago`;
  }
  const days: number = Math.floor(diffMs / 86_400_000);
  if (days < 7) {
    return `${days} ${plural(days, "day")} ago`;
  }
  const formatted: string = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(parsed));
  return `on ${formatted}`;
}

function plural(n: number, unit: string): string {
  return n === 1 ? unit : `${unit}s`;
}
